import * as React from 'react';
import type { FieldPath, FieldValues, UseControllerProps, UseFormUnregister } from 'react-hook-form';

import type { OwnProps as PlainDropdownProps, DropdownOptionType } from 'af-components/Controls/Dropdown';

import DropdownCell from '../../RHFTableCellComponents/Dropdown/DropdownCell';
import type { RenderMethodParams } from '../RHFFieldCellWrapper';
import RHFFieldWrapper from '../RHFFieldCellWrapper';

type Props<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues = TFieldValues
> = {
	rhfId: number | string;
	onChange: (name: string) => void;
	onBlur: () => void;
	unregister: UseFormUnregister<TFieldValues>;
	openFocused?: boolean;
	bold?: boolean;
} & UseControllerProps<TFieldValues, TName, TTransformedValues> & PlainDropdownProps<D>;

function DropdownField<
	D extends DropdownOptionType,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues
>(props: Props<D, TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, onChange, onBlur, openFocused = false, bold, ...rest } = props;

	const renderDropdownCell = React.useCallback((
		{
			field,
			error,
			onFieldChange,
			onFieldBlur,
		}: RenderMethodParams<TFieldValues, TName>
	) => (
		<DropdownCell
			bold={bold}
			elementKey={rhfId}
			error={error ?? undefined}
			key={name + rhfId}
			name={field.name}
			onBlur={onFieldBlur}
			onChange={onFieldChange}
			placeholder={placeholder}
			value={field.value}
			{...rest}
			autoOpen={openFocused}
		/>
	), [bold, rhfId, name, placeholder, rest, openFocused]);

	return (
		<RHFFieldWrapper
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			{...rest}
			render={renderDropdownCell}
		/>
	);
}

export default React.memo(DropdownField) as typeof DropdownField;
